import React from 'react';
import { useHistory } from 'react-router-dom';
import { IonCard, IonCardContent, IonIcon, IonText, IonButton, IonSpinner } from '@ionic/react';
import { water, flowerOutline, timeOutline } from 'ionicons/icons';
import { DAY_LABELS, type ValveSummary } from '../../../domain/Irrigation';
import { formatRelative } from '../../../domain/WeatherStation';
import { useStationValves } from '../../../application/use-cases/irrigation/useStationValves';
import './station.css';

const formatTime = (iso: string) => new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });

const minutesBetween = (from: string, to: string) => Math.max(1, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 60000));

function valveStatus(v: ValveSummary): string {
  if (v.running?.startedAt) return `Regando desde las ${formatTime(v.running.startedAt)}`;
  const parts: string[] = [];
  const last = v.lastFinished;
  if (last?.startedAt && last.endedAt) parts.push(`Último: ${formatRelative(last.endedAt)} · ${minutesBetween(last.startedAt, last.endedAt)} min`);
  if (v.schedule?.enabled) parts.push(`${v.schedule.daysOfWeek.map((d) => DAY_LABELS[d]).join(' ')} ${v.schedule.startTime}`);
  else parts.push(v.schedule ? 'Horario pausado' : 'Sin horario');
  return parts.join(' · ');
}

interface Props {
  deviceId: string;
  canControl: boolean;
}

// Una fila por válvula de la estación: estado, regar/detener y accesos a
// su horario e historial. Lo usan el home y el detalle de la estación.
const StationValves: React.FC<Props> = ({ deviceId, canControl }) => {
  const history = useHistory();
  const { valves, loading, busyChannel, error, open, close } = useStationValves(deviceId);

  return (
    <IonCard className="station-valves">
      <IonCardContent>
        <div className="station-section-head">
          <span>Riego</span>
          {loading && <IonSpinner name="dots" className="station-inline-spinner" />}
        </div>
        {error && (
          <IonText color="danger">
            <p style={{ fontSize: 12.5, marginTop: 0 }}>{error}</p>
          </IonText>
        )}
        {valves.map((v) => {
          const running = !!v.running;
          const base = `/app/devices/${deviceId}/valves/${v.channel}/irrigation`;
          return (
            <div className={`station-valve ${running ? 'running' : ''}`} key={v.channel}>
              <IonIcon icon={running ? water : flowerOutline} className="station-valve-icon" />
              <div className="station-valve-info">
                <p className="station-valve-name">{v.name}</p>
                <p className="station-valve-status">{valveStatus(v)}</p>
                <div className="station-valve-links">
                  <button onClick={() => history.push(base)}>
                    <IonIcon icon={timeOutline} />
                    Horario
                  </button>
                  <button onClick={() => history.push(`${base}/history`)}>Historial</button>
                </div>
              </div>
              {canControl && (
                <IonButton
                  size="small"
                  color={running ? 'danger' : 'primary'}
                  fill={running ? 'outline' : 'solid'}
                  disabled={busyChannel !== null}
                  onClick={() => (running ? close(v.channel) : open(v.channel))}
                >
                  {busyChannel === v.channel ? <IonSpinner name="dots" /> : running ? 'Detener' : 'Regar'}
                </IonButton>
              )}
            </div>
          );
        })}
      </IonCardContent>
    </IonCard>
  );
};

export default StationValves;
